declare const I18n: any;

import React, { useState } from "react";
import Header from "@/components/Header";
import BreadCrumb from "@/components/BreadCrumb";
import TextInput from '@/components/inputs/TextInput';
import SelectInput from '@/components/inputs/SelectInput';
import NoteInput from '@/components/inputs/NoteInput';
import SingleFileUpload from '@/components/forms/SingleFileUpload';
import Toast from "@/components/Toast/toast";
import api from "../../../api";
import { string } from "prop-types";

type ShowBadgeProps = {
  badge: any
  skill: any
};

const ShowBadge: React.FC<ShowBadgeProps> = ({ badge, skill }) => {
  const [form, setForm] = useState({
    name: badge.name,
    skillId: skill.id,
    pointsRequired: badge.pointsRequired,
    descriptionEn: badge.description_english,
    descriptionFr: badge.description_french,
    imageFile: null as File | null,
    learnMoreUrlEnglish: badge.learn_more_url_english || "",
    learnMoreUrlFrench: badge.learn_more_url_french || "",
  });

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "info" | "danger";
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateForm = () => {
    if (!form.name.trim()) {
      return I18n.t('views.achievements.badge.messaging.errors.name_required');
    }

    if (!form.skillId) {
      return I18n.t('views.achievements.badge.messaging.errors.skill_required');
    }

    if (!form.pointsRequired) {
      return I18n.t('views.achievements.badge.messaging.errors.points_required');
    }

    const points = Number(form.pointsRequired);

    if (Number.isNaN(points)) {
      return I18n.t('views.achievements.badge.messaging.errors.points_required');
    }

    if (!Number.isInteger(points) || points <= 0) {
      return I18n.t('views.achievements.badge.messaging.errors.points_required');
    }

    const allowedTypes = ["image/png", "image/svg+xml"];

    if (form.imageFile && !allowedTypes.includes(form.imageFile.type)) {
      return I18n.t('views.achievements.badge.messaging.errors.image_not_supported');
    }

    return null;
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errorMessage = validateForm();

    if (errorMessage) {
      setToast({
        message: errorMessage,
        type: "danger",
      });
      return;
    }


    const formData = new FormData();

    formData.append("badge[name]", form.name);
    formData.append("badge[skill_id]", form.skillId);
    formData.append("badge[points_required]", form.pointsRequired);
    formData.append("badge[description_en]", form.descriptionEn);
    formData.append("badge[description_fr]", form.descriptionFr);
    formData.append("badge[learn_more_url_english]", form.learnMoreUrlEnglish);
    formData.append("badge[learn_more_url_french]", form.learnMoreUrlFrench);

    if (form.imageFile) {
      formData.append("badge[image]", form.imageFile);
    }

    try {
      const response = await api.updateBadge(badge.id, formData);
      setError(null);
      setToast({
        message: I18n.t('views.achievements.badge.messaging.updated'),
        type: "info",
      });
    } catch (err) {
      setToast({
        message: I18n.t('views.achievements.badge.messaging.errors.update_fail'),
        type: "danger",
      });
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="show-badge">
        <Header title={I18n.t("views.nav.achievements")} showIcon={false} />
        <BreadCrumb
          locations={[
            { title: I18n.t('views.nav.achievements'), url: '/admin/achievements' },
            { title: I18n.t('models.badge.messaging.edit'), url: null }
          ]}
        />      <form onSubmit={handleSubmit}>
          {/* Row 1 */}
          <div className="col-md-12">
            <div className="row">
              <TextInput
                columnSize={6}
                value={form.name}
                error=""
                type="badge"
                field="name"
                label={I18n.t('views.achievements.badge.labels.name')}
                className="required"
                onChange={(value) =>
                  setForm({ ...form, name: value })
                }
              />

              <div className="col-md-6">
                <SingleFileUpload
                  parent="badge"
                  field="image"
                  label={I18n.t('views.achievements.badge.labels.image')}
                  required
                  onChange={(files) =>
                    setForm({ ...form, imageFile: files[0] })
                  }
                  onDelete={() =>
                    setForm({ ...form, imageFile: null })
                  }
                />
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="col-md-12">
            <div className="row">
              <SelectInput
                value={form.skillId}
                error=""
                type="badge"
                field="skill_id"
                label={I18n.t('views.achievements.badge.labels.skill')}
                columnSize={6}
                onChange={(value) =>
                  setForm({ ...form, skillId: value })
                }
                className="required"
              >
                <option key={skill.id} value={skill.id}>
                  {I18n.t(`models.skills.attributes.${skill.slug}`)}
                </option>
              </SelectInput>

              <TextInput
                columnSize={6}
                value={form.pointsRequired}
                error=""
                type="badge"
                field="points_required"
                label={I18n.t('views.achievements.badge.labels.points_required')}
                className="required"
                onChange={(value) =>
                  setForm({ ...form, pointsRequired: value })
                }
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="col-md-12">
            <div className="row">
              <TextInput
                columnSize={6}
                value={form.learnMoreUrlEnglish}
                error=""
                type="badge"
                field="learn_more_url_english"
                label={I18n.t('views.achievements.badge.labels.learn_more_url_en')}
                onChange={(value) =>
                  setForm({ ...form, learnMoreUrlEnglish: value })
                }
              />

              <TextInput
                columnSize={6}
                value={form.learnMoreUrlFrench}
                error=""
                type="badge"
                field="learn_more_url_french"
                label={I18n.t('views.achievements.badge.labels.learn_more_url_fr')}
                onChange={(value) =>
                  setForm({ ...form, learnMoreUrlFrench: value })
                }
              />
            </div>
          </div>

          {/* Descriptions */}
          <NoteInput
            value={form.descriptionEn}
            error=""
            type="badge"
            field="description_en"
            label={I18n.t('views.achievements.badge.labels.description_en')}
            required={false}
            toolbarOptions={{}}
            onChange={(value) =>
              setForm({ ...form, descriptionEn: value })
            }
            className="required"
          />

          <NoteInput
            value={form.descriptionFr}
            error=""
            type="badge"
            field="description_fr"
            label={I18n.t('views.achievements.badge.labels.description_fr')}
            required={false}
            toolbarOptions={{}}
            onChange={(value) =>
              setForm({ ...form, descriptionFr: value })
            }
            className="required"
          />

          {/* Submit */}
          <div className="mt-4">
            <button type="submit" className="btn btn-primary">
              {I18n.t('views.achievements.badge.labels.update_badge')}
            </button>
          </div>
        </form>
      </div></>
  );
}

export default ShowBadge;